import { supabase } from '../lib/supabase'

/**
 * Shot reference images, stored for real.
 *
 * Mirrors callSheetStore.ts's approach for call sheet PDFs — upload a File
 * straight to Supabase Storage and keep only the resulting public URL,
 * rather than asking whoever fills in the shot list to go find and host an
 * image somewhere else and paste its URL in (Tom's 9 Sep 2026 request: "a
 * URL upload is going to be hard to do. Can it be a simple image upload?").
 */

const BUCKET = 'shot-references'
const MAX_BYTES = 10 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

function sanitize(name: string): string {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-')
  return cleaned.slice(-120) || 'reference.jpg'
}

function id(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export interface ShotReferenceUploadOutcome {
  ok: boolean
  url?: string
  error?: string
}

export async function uploadShotReference(projectId: string, file: File): Promise<ShotReferenceUploadOutcome> {
  if (!supabase) return { ok: false, error: 'No database connection.' }
  if (!projectId) return { ok: false, error: 'Save the project before adding a reference image.' }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { ok: false, error: 'Reference images must be a JPEG, PNG, WEBP or GIF.' }
  }
  if (file.size > MAX_BYTES) return { ok: false, error: 'That image is larger than 10MB.' }

  const path = `${projectId}/${id()}-${sanitize(file.name)}`

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false,
  })

  if (error) {
    console.warn('[supabase] shot reference upload failed:', error.message)
    return { ok: false, error: 'That image could not be saved. Nothing was stored.' }
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return { ok: true, url: data.publicUrl }
}
